import React, { useState, useCallback, useEffect, useRef } from 'react';
import { isAxiosError } from 'axios';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Video, AlertCircle, Play, Square, CheckCircle, Eye, ArrowRight } from 'lucide-react';
import DesktopClipsPanel from '../components/DesktopClipsPanel';
import { useDesktopSessionVideos } from '../hooks/useDesktopSessionVideos';
import { useRecordingConfig } from '../hooks/useRecordingConfig';
import PopupRecordingFrame from '../components/PopupRecordingFrame';
import PostRecordingForm from '../components/PostRecordingForm';
import PreRecordingForm from '../components/PreRecordingForm';
import TicketRecordingHeader from '../components/TicketRecordingHeader';
import VideoProgressModal from '../components/VideoProgressModal';
import VideoSortableList from '../components/VideoSortableList';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import { useScreenCapture, RecordedVideo } from '../hooks/useScreenCapture';
import { api, ensureCsrfReady, readApiErrorMessage } from '../hooks/useAuth';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTicket, useLinkSessionToTicket } from '../hooks/useTickets';
import { normalizeStatus } from '../utils/roles';

interface SessionFormData {
  title: string;
  description: string;
  notes: string;
}

/** Legacy ad-hoc flow only; ticket recordings skip this. */
interface PreRecordingData {
  phobiaType?: string;
  exposureLevel?: number;
  triggers?: string[];
  sessionGoals?: string;
}

const NewSession: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get('ticketId');
  const sessionIdFromUrl = searchParams.get('sessionId');
  const hasTicketFlow = !!ticketId;
  const { data: ticketData, error: ticketError, isLoading: ticketLoading } = useTicket(ticketId || '');
  const linkSessionMutation = useLinkSessionToTicket();
  const ticketInitStarted = useRef(false);
  const [ticketInitError, setTicketInitError] = useState<string | null>(null);
  const [ticketInitAttempt, setTicketInitAttempt] = useState(0);

  const [showPreRecordingForm, setShowPreRecordingForm] = useState(!hasTicketFlow && !sessionIdFromUrl);
  const [ticketInitializing, setTicketInitializing] = useState(hasTicketFlow);
  const [desktopSessionInitializing, setDesktopSessionInitializing] = useState(!!sessionIdFromUrl && !hasTicketFlow);
  const [desktopSessionInitAttempt, setDesktopSessionInitAttempt] = useState(0);
  const desktopSessionInitStarted = useRef(false);
  const [showRecordingFrame, setShowRecordingFrame] = useState(false);
  const [showPostRecordingForm, setShowPostRecordingForm] = useState(false);
  const [combinedVideoBlob, setCombinedVideoBlob] = useState<Blob | null>(null);
  const [combinedVideoDuration, setCombinedVideoDuration] = useState(0);
  const [isCombining, setIsCombining] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [orderedVideos, setOrderedVideos] = useState<RecordedVideo[]>([]);
  const [preRecordingData, setPreRecordingData] = useState<PreRecordingData | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  
  // Progress tracking states
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressStep, setProgressStep] = useState<'combining' | 'uploading' | 'saving' | 'completed'>('combining');
  const [progressValue, setProgressValue] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  
  const { 
    isRecording: isScreenRecording, 
    isSupported, 
    error, 
    captureWarning,
    previewStream,
    recordedVideos,
    currentRecordingDuration,
    startRecording, 
    stopRecording, 
    getAllRecordedVideos,
    clearRecordedVideos 
  } = useScreenCapture();
  const queryClient = useQueryClient();
  const { desktopRecorderOnly } = useRecordingConfig();
  const {
    data: desktopSession,
    isLoading: desktopClipsLoading,
    isError: desktopClipsError,
    error: desktopClipsQueryError,
    refetch: refetchDesktopClips,
  } = useDesktopSessionVideos(
    currentSessionId,
    sessionActive && desktopRecorderOnly
  );
  const desktopClips = desktopSession?.videos ?? [];
  const desktopSessionMissing =
    desktopClipsError &&
    isAxiosError(desktopClipsQueryError) &&
    desktopClipsQueryError.response?.status === 404;

  const desktopWorkflowFinishedRef = useRef(false);

  useEffect(() => {
    if (sessionActive) desktopWorkflowFinishedRef.current = false;
  }, [sessionActive, currentSessionId]);

  useEffect(() => {
    if (!sessionActive || !desktopSession || desktopWorkflowFinishedRef.current) return;

    const sessionDone =
      desktopSession.sessionStatus === 'completed' || desktopSession.sessionStatus === 'cancelled';
    const ticketNorm = desktopSession.ticketStatus
      ? normalizeStatus(desktopSession.ticketStatus)
      : null;
    const ticketDone =
      ticketNorm === 'completed' ||
      ticketNorm === 'sent' ||
      ticketNorm === 'failed' ||
      ticketNorm === 'cancelled';

    if (!sessionDone && !ticketDone) return;

    desktopWorkflowFinishedRef.current = true;
    setSessionActive(false);
    queryClient.invalidateQueries('sessions');
    queryClient.invalidateQueries('in-progress-videos');
    if (ticketId) queryClient.invalidateQueries(['ticket', ticketId]);

    if (ticketNorm === 'completed' || ticketNorm === 'sent') {
      toast.success('Video is ready — open the ticket to send it to the customer.', {
        id: 'desktop-workflow-done',
        duration: 8000,
      });
    } else if (sessionDone) {
      toast.success('Recording session finished.', { id: 'desktop-workflow-done' });
    }
  }, [sessionActive, desktopSession, ticketId, queryClient]);

  const formatDuration = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    if (mb < 1) {
      return `${(mb * 1024).toFixed(0)} KB`;
    }
    return `${mb.toFixed(1)} MB`;
  };

  const handleStartRecording = useCallback(async () => {
    try {
      setSessionActive(true);
      await startRecording({
        width: 1920,
        height: 1080,
        frameRate: 30,
        quality: 2.5
      });
      toast.success('Recording started');
    } catch (err) {
      toast.error('Failed to start recording');
      console.error('Recording error:', err);
    }
  }, [startRecording]);

  useEffect(() => {
    if (captureWarning) {
      toast.error(captureWarning, { duration: 12000 });
    }
  }, [captureWarning]);

  const handleStopRecording = useCallback(() => {
    stopRecording();
    toast.success('Recording stopped. You can start another recording or end the session.');
  }, [stopRecording]);

  const handleCloseRecordingFrame = useCallback(() => {
    if (isScreenRecording) {
      stopRecording();
    }
    setShowRecordingFrame(false);
  }, [isScreenRecording, stopRecording]);

  const handleOpenRecordingFrame = useCallback(() => {
    setShowRecordingFrame(true);
    setSessionActive(true);
  }, []);

  const handleDeleteRecording = useCallback((videoId: string) => {
    // Filter out the deleted video from both recordedVideos and orderedVideos
    const updatedOrderedVideos = orderedVideos.filter(v => v.id !== videoId);
    
    // Update the ordered videos state
    setOrderedVideos(updatedOrderedVideos);
    
    // Note: We can't directly remove from hook state, so we'll handle this in the combine logic
    toast.success('Recording removed from session');
  }, [orderedVideos]);

  const handleVideosReorder = useCallback((reorderedVideos: RecordedVideo[]) => {
    setOrderedVideos(reorderedVideos);
    toast.success('Video order updated');
  }, []);

  const handleToggleVideoPreview = useCallback(() => {
    if (!showVideoPreview && recordedVideos.length > 0) {
      // Initialize ordered videos with current recorded videos
      setOrderedVideos([...recordedVideos]);
    }
    setShowVideoPreview(!showVideoPreview);
  }, [showVideoPreview, recordedVideos]);

  const handleEndSession = useCallback(async () => {
    // Use ordered videos if available, otherwise fall back to recorded videos
    const videos = orderedVideos.length > 0 ? orderedVideos : getAllRecordedVideos();
    const useDesktopClips =
      videos.length === 0 && !!currentSessionId && (desktopRecorderOnly || desktopClips.length > 0);

    if (videos.length === 0 && !currentSessionId) {
      toast.error('No recordings found. Please record at least one video before ending the session.');
      return;
    }

    setIsCombining(true);
    setShowProgressModal(true);
    setProgressStep('combining');
    setProgressValue(0);
    setProgressMessage('Preparing videos for rendering...');

    try {
      setProgressValue(5);
      setProgressMessage(
        useDesktopClips
          ? 'Combining clips uploaded from desktop recorder…'
          : 'Preparing videos for rendering...'
      );

      let isUploadComplete = false;

      const serverProgressTimer = window.setInterval(() => {
        setProgressValue((prev) => (prev < 88 ? Math.min(88, prev + 0.4) : prev));
        setProgressMessage(
          'Processing on server (normalizing, combining, logo & audio — may take 15–40 minutes)...'
        );
      }, 3000);

      let combineResponse;
      try {
        if (useDesktopClips) {
          await ensureCsrfReady();
          const combineUrl = ticketId
            ? `/videos/combine-session/${currentSessionId}?ticketId=${encodeURIComponent(ticketId)}`
            : `/videos/combine-session/${currentSessionId}`;
          setProgressValue(15);
          setProgressMessage('Combining session videos on server…');
          combineResponse = await api.post(combineUrl, null, { timeout: 0 });
        } else {
          const formData = new FormData();
          videos.forEach((video, index) => {
            formData.append('videos', video.blob, `recording-${index + 1}.webm`);
          });
          if (ticketId) {
            formData.append('ticketId', ticketId);
          }

          setProgressValue(10);
          setProgressMessage('Uploading videos...');

          combineResponse = await api.post('/videos/combine', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            timeout: 0,
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total && !isUploadComplete) {
                const uploadProgressPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                const combinedProgress = 10 + uploadProgressPercent * 0.1;
                setProgressValue(combinedProgress);
                setProgressMessage(`Uploading videos... ${uploadProgressPercent}%`);
              }
            },
          });
        }
      } finally {
        window.clearInterval(serverProgressTimer);
      }

      isUploadComplete = true;

      setProgressValue(88);
      setProgressMessage('Rendering complete. Downloading...');

      console.log('Videos combined:', combineResponse.data);
      
      // Step 4: Download rendered video (90-95%)
      setProgressValue(90);
      setProgressMessage('Downloading rendered video...');
      
      const combinedVideoInfo = combineResponse.data.combinedVideo;
      
      const downloadResponse = await api.get(`/videos/temp/${combinedVideoInfo.filename}`, {
        responseType: 'blob',
        timeout: 0,
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const downloadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            const combinedProgress = 90 + (downloadProgress * 0.05); // 90-95% range
            setProgressValue(combinedProgress);
            setProgressMessage(`Downloading rendered video... ${downloadProgress}%`);
          }
        }
      });
      
      // Step 5: Complete (95-100%)
      setProgressValue(95);
      setProgressMessage('Finalizing...');
      
      const rawBlob = downloadResponse.data as Blob;
      const combinedMp4Blob =
        rawBlob.type === 'video/mp4'
          ? rawBlob
          : new Blob([rawBlob], { type: 'video/mp4' });
      setCombinedVideoBlob(combinedMp4Blob);
      const probedSeconds =
        typeof combinedVideoInfo.durationSeconds === 'number' && combinedVideoInfo.durationSeconds > 0
          ? combinedVideoInfo.durationSeconds
          : 0;
      setCombinedVideoDuration(
        probedSeconds > 0
          ? probedSeconds
          : videos.length > 0
            ? videos.reduce((sum, v) => sum + v.duration, 0)
            : 0
      );
      
      // Complete
      setProgressValue(100);
      setProgressStep('completed');
      const clipCount = useDesktopClips
        ? (combineResponse.data?.clipCount ?? 'session')
        : videos.length;
      setProgressMessage(`Successfully rendered ${clipCount} video(s)!`);
      
      // Close recording frame and show form after a brief delay
      setTimeout(() => {
        setShowProgressModal(false);
        setShowRecordingFrame(false);
        setShowPostRecordingForm(true);
        setIsCombining(false);
      }, 1500);

    } catch (error: any) {
      console.error('Error rendering videos:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        timeout: error.code === 'ECONNABORTED'
      });
      setIsCombining(false);
      setShowProgressModal(false);
      
      let errorMessage = 'Failed to render videos';
      if (error.code === 'ECONNABORTED') {
        errorMessage =
          'Video rendering timed out or the connection was interrupted. Keep this tab open and try again; processing can take 30+ minutes for long sessions.';
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (typeof error.response?.data?.message === 'string') {
        errorMessage = error.response.data.message;
      } else if (typeof error.response?.data?.error === 'string') {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      
      // Reset state on error to prevent stuck loading
      setShowRecordingFrame(false);
      setShowPostRecordingForm(false);
      setCombinedVideoBlob(null);
      setCombinedVideoDuration(0);
      setSessionActive(false);
      // Don't clear recorded videos on error - keep them for retry
      // clearRecordedVideos();
    }
  }, [orderedVideos, getAllRecordedVideos, ticketId, currentSessionId, desktopRecorderOnly, desktopClips.length]);

  const handleSaveSession = useCallback(async (data: SessionFormData, videoBlob: Blob) => {
    try {
      console.log('Saving session with data:', data);
      console.log('Video blob:', videoBlob);
      console.log('Video blob size:', videoBlob?.size);
      console.log('Video blob type:', videoBlob?.type);
      
      // Check if video blob is valid
      if (!videoBlob || videoBlob.size === 0) {
        console.error('Invalid video blob:', { blob: videoBlob, size: videoBlob?.size });
        throw new Error('No video data available. Please record a session first.');
      }
      
      // Show progress modal for saving
      setShowProgressModal(true);
      setProgressStep('saving');
      setProgressValue(0);
      setProgressMessage('Preparing session data...');
      
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('video', videoBlob, 'session-recording.mp4');
      formData.append('duration', combinedVideoDuration.toString());
      
      console.log('Recording duration being sent:', combinedVideoDuration, 'seconds');
      
      const legacyPhobiaTypeId = !ticketId && preRecordingData?.phobiaType?.trim()
        ? preRecordingData.phobiaType.trim()
        : undefined;

      // Step 1: Update existing session or create new one (10-30%)
      setProgressValue(10);
      setProgressMessage(currentSessionId ? 'Updating session...' : 'Creating session...');
      
      let sessionId = currentSessionId;
      
      if (currentSessionId) {
        // Update the existing session that was created when recording started
        try {
          const updatePayload: Record<string, unknown> = {
            title: data.title,
            description: data.description,
            durationMinutes: Math.max(1, Math.round(combinedVideoDuration / 60)),
            notes: data.notes,
            status: 'completed',
          };
          if (!ticketId && preRecordingData?.exposureLevel) {
            updatePayload.anxietyLevelStart = preRecordingData.exposureLevel;
            updatePayload.anxietyLevelEnd = preRecordingData.exposureLevel;
            updatePayload.triggers = preRecordingData.triggers || [];
            updatePayload.sessionGoals = preRecordingData.sessionGoals || '';
          }
          const updateResponse = await api.put(`/sessions/${currentSessionId}`, updatePayload);
          console.log('Session updated:', updateResponse.data);
        } catch (error: any) {
          console.error('Failed to update session, creating new one:', error);
          console.error('Error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            code: error.code
          });
          // Only fall back if it's not a validation error - validation errors should be shown to user
          if (error.response?.status === 400 || error.response?.status === 404) {
            // Don't fall back on validation errors - throw to show user
            throw error;
          }
          sessionId = null; // Fall back to creating new session for network errors
        }
      }
      
      if (!sessionId) {
        // Create new session if update failed or no session exists
        const createPayload: Record<string, unknown> = {
          title: data.title,
          description: data.description,
          sessionDate: new Date().toISOString(),
          durationMinutes: Math.max(1, Math.round(combinedVideoDuration / 60)),
          notes: data.notes,
          status: 'completed',
        };
        if (ticketId) {
          createPayload.ticketId = ticketId;
        } else if (legacyPhobiaTypeId) {
          createPayload.phobiaTypeId = legacyPhobiaTypeId;
          if (preRecordingData?.exposureLevel) {
            createPayload.anxietyLevelStart = preRecordingData.exposureLevel;
            createPayload.anxietyLevelEnd = preRecordingData.exposureLevel;
            createPayload.triggers = preRecordingData.triggers || [];
            createPayload.sessionGoals = preRecordingData.sessionGoals || '';
          }
        }
        const sessionResponse = await api.post('/sessions', createPayload);
        
        console.log('Session created:', sessionResponse.data);
        sessionId = sessionResponse.data.sessionId || sessionResponse.data.session?.id || sessionResponse.data.id;
        
        if (ticketId && sessionId) {
          try {
            const tStatus = ticketData?.ticket ? normalizeStatus(ticketData.ticket.status) : null;
            if (tStatus === 'open') {
              try {
                await api.post(`/tickets/${ticketId}/accept`);
              } catch (acceptErr: unknown) {
                const acceptMsg = readApiErrorMessage(acceptErr, '');
                if (!/only open tickets can be accepted/i.test(acceptMsg)) {
                  throw acceptErr;
                }
              }
            }
            await linkSessionMutation.mutateAsync({ ticketId, sessionId });
          } catch (error) {
            console.error('Failed to link session to ticket:', error);
          }
        }
      }
      
      // Step 2: Upload video (30-90%)
      setProgressValue(30);
      setProgressMessage('Uploading video...');
      
      try {
        // Calculate timeout based on video size (5 minutes base + 1 minute per 50MB)
        const videoSizeMB = videoBlob.size / (1024 * 1024);
        const timeoutMs = Math.max(300000, 300000 + (videoSizeMB / 50) * 60000); // Min 5 min, +1 min per 50MB
        
        console.log('Uploading video:', {
          size: videoBlob.size,
          sizeMB: videoSizeMB.toFixed(2),
          timeout: timeoutMs / 1000,
          sessionId
        });
        
        const videoResponse = await api.post(`/videos/upload/${sessionId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: timeoutMs, // Increased timeout for large files
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              const combinedProgress = 30 + (uploadProgress * 0.6); // 30-90% range
              setProgressValue(combinedProgress);
              setProgressMessage(`Uploading video... ${uploadProgress}%`);
            } else {
              // Show indeterminate progress
              setProgressMessage('Uploading video...');
            }
          }
        });
        
        console.log('Video uploaded successfully:', videoResponse.data);
        
        // Step 3: Complete (90-100%)
        setProgressValue(90);
        setProgressMessage('Finalizing...');
        
        // Reset current session ID since session is now complete
        setCurrentSessionId(null);
        
        // Invalidate sessions query to refresh the sessions list
        queryClient.invalidateQueries('sessions');
        queryClient.invalidateQueries('in-progress-videos');
        queryClient.invalidateQueries('user-stats');
        
        // Complete
        setProgressValue(100);
        setProgressStep('completed');
        setProgressMessage('Session and video saved successfully!');
        
        // Reset state after completion
        setTimeout(() => {
          setShowProgressModal(false);
          setShowPostRecordingForm(false);
          setShowRecordingFrame(false);
          setCombinedVideoBlob(null);
          setCombinedVideoDuration(0);
          setSessionActive(false);
          clearRecordedVideos();
          
          // Navigate to sessions page
          window.location.href = '/sessions';
        }, 1500);
        
      } catch (videoError: any) {
        console.error('Video upload error:', videoError);
        console.error('Video upload error details:', {
          message: videoError.message,
          response: videoError.response?.data,
          status: videoError.response?.status,
          code: videoError.code,
          config: videoError.config
        });
        
        queryClient.invalidateQueries('sessions');
        setShowProgressModal(false);
        
        let videoErrorMessage = 'Video upload failed';
        if (videoError.code === 'ECONNABORTED' || videoError.code === 'ERR_NETWORK') {
          videoErrorMessage = 'Network error - upload timed out. Please try again with a smaller file or check your connection.';
        } else if (videoError.response?.data?.error?.message) {
          videoErrorMessage = videoError.response.data.error.message;
        } else if (videoError.response?.data?.message) {
          videoErrorMessage = videoError.response.data.message;
        } else if (videoError.message) {
          videoErrorMessage = videoError.message;
        }
        
        toast.error(`Session saved, but video upload failed: ${videoErrorMessage}`);
      }
      
    } catch (error: any) {
      console.error('Error saving session:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        code: error.code,
        config: error.config
      });
      
      setIsCombining(false);
      setShowProgressModal(false);
      
      let errorMessage = 'Failed to save session';
      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      toast.error(`Failed to save session: ${errorMessage}`);
      
      // Reset state on error to prevent stuck loading
      setShowPostRecordingForm(false);
      setShowRecordingFrame(false);
      setCombinedVideoBlob(null);
      setCombinedVideoDuration(0);
      setSessionActive(false);
      // Don't clear recorded videos on error - keep them for retry
      // clearRecordedVideos();
    }
  }, [combinedVideoDuration, queryClient, clearRecordedVideos, currentSessionId, preRecordingData, ticketId, linkSessionMutation, ticketData]);

  const initializeRecordingSession = useCallback(
    async (data: PreRecordingData, options?: { openRecordingFrame?: boolean }) => {
      setPreRecordingData(data);

      let ticket = ticketData?.ticket;
      if (ticketId) {
        try {
          const { data: fresh } = await api.get(`/tickets/${ticketId}`);
          if (fresh?.ticket) ticket = fresh.ticket;
        } catch {
          /* use cached ticket from useTicket */
        }
      }

      if (ticket?.sessionId) {
        try {
          await api.get(`/sessions/${ticket.sessionId}`);
          setCurrentSessionId(ticket.sessionId);
          setShowPreRecordingForm(false);
          setSessionActive(true);
          queryClient.invalidateQueries('tickets');
          if (options?.openRecordingFrame !== false) {
            setShowRecordingFrame(true);
          }
          return;
        } catch (verifyErr: unknown) {
          const missing =
            isAxiosError(verifyErr) && verifyErr.response?.status === 404;
          if (!missing) throw verifyErr;
          // Ticket points at a deleted/missing session — create a new one below.
        }
      }

      if (sessionIdFromUrl && !ticketId) {
        try {
          await api.get(`/sessions/${sessionIdFromUrl}`);
          setCurrentSessionId(sessionIdFromUrl);
          setShowPreRecordingForm(false);
          setSessionActive(true);
          if (options?.openRecordingFrame !== false) {
            setShowRecordingFrame(true);
          }
          return;
        } catch (verifyErr: unknown) {
          const missing =
            isAxiosError(verifyErr) && verifyErr.response?.status === 404;
          if (!missing) throw verifyErr;
        }
      }

      const sessionTitle = ticketData?.ticket?.title || `Recording Session - ${new Date().toLocaleString()}`;
      const sessionDescription =
        ticketData?.ticket?.description ||
        ticketData?.ticket?.notes ||
        `Recording session — ${new Date().toLocaleString()}`;

      const createBody: Record<string, unknown> = {
        title: sessionTitle,
        description: sessionDescription,
        sessionDate: new Date().toISOString(),
        durationMinutes: 0,
        notes: ticketData?.ticket?.notes || '',
        status: 'in_progress',
      };
      if (ticketId) {
        createBody.ticketId = ticketId;
      } else if (data.phobiaType?.trim()) {
        createBody.phobiaTypeId = data.phobiaType.trim();
        if (data.exposureLevel) {
          createBody.anxietyLevelStart = data.exposureLevel;
          createBody.anxietyLevelEnd = data.exposureLevel;
          createBody.triggers = data.triggers || [];
          createBody.sessionGoals = data.sessionGoals || '';
        }
      }

      await ensureCsrfReady();
      const sessionResponse = await api.post('/sessions', createBody);

      const sessionId =
        sessionResponse.data.sessionId || sessionResponse.data.session?.id || sessionResponse.data.id;
      setCurrentSessionId(sessionId);

      if (ticketId && sessionId) {
        const tStatus = ticket ? normalizeStatus(ticket.status) : null;
        if (tStatus === 'open') {
          try {
            await api.post(`/tickets/${ticketId}/accept`);
          } catch (acceptErr: unknown) {
            const acceptMsg = readApiErrorMessage(acceptErr, '');
            if (!/only open tickets can be accepted/i.test(acceptMsg)) {
              throw acceptErr;
            }
          }
        }
        await linkSessionMutation.mutateAsync({ ticketId, sessionId });
      }

      queryClient.invalidateQueries('sessions');
      queryClient.invalidateQueries('in-progress-videos');
      queryClient.invalidateQueries('user-stats');
      queryClient.invalidateQueries('tickets');

      setShowPreRecordingForm(false);
      setSessionActive(true);
      if (options?.openRecordingFrame !== false) {
        setShowRecordingFrame(true);
      }
    },
    [queryClient, ticketId, ticketData, linkSessionMutation, sessionIdFromUrl]
  );

  const handleStartTicketRecordingSession = useCallback(async () => {
    try {
      await initializeRecordingSession({}, { openRecordingFrame: false });
      toast.success('Recording session ready — open the desktop recorder.');
    } catch (error: unknown) {
      toast.error(readApiErrorMessage(error, 'Could not start recording session.'));
    }
  }, [initializeRecordingSession]);

  const retryTicketInit = useCallback(() => {
    ticketInitStarted.current = false;
    setTicketInitError(null);
    setTicketInitAttempt((n) => n + 1);
  }, []);

  const handleResetRecordingSession = useCallback(async () => {
    if (!hasTicketFlow && !sessionIdFromUrl) {
      toast.error('Start a session from the form or a ticket first.');
      return;
    }
    ticketInitStarted.current = false;
    setCurrentSessionId(null);
    setSessionActive(false);
    setTicketInitError(null);
    try {
      await initializeRecordingSession({}, { openRecordingFrame: false });
      toast.success('New recording session created.');
    } catch (error: unknown) {
      toast.error(readApiErrorMessage(error, 'Could not create a new session.'));
    }
  }, [hasTicketFlow, sessionIdFromUrl, initializeRecordingSession]);

  useEffect(() => {
    if (!hasTicketFlow || ticketLoading || !ticketData?.ticket || ticketInitStarted.current) return;
    ticketInitStarted.current = true;

    (async () => {
      setTicketInitializing(true);
      setTicketInitError(null);
      try {
        await initializeRecordingSession({}, { openRecordingFrame: false });
      } catch (error: unknown) {
        console.error('Failed to initialize ticket recording session:', error);
        const err = error as { response?: { data?: { error?: { message?: string } } }; message?: string };
        const detail =
          err.response?.data?.error?.message ||
          err.message ||
          'Failed to prepare recording session.';
        setTicketInitError(detail);
        toast.error('Failed to prepare recording session. Try again from My Tickets.', {
          id: 'ticket-recording-init',
        });
      } finally {
        setTicketInitializing(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per ticket load, not on every ticketData refresh
  }, [
    hasTicketFlow,
    ticketLoading,
    ticketId,
    ticketInitAttempt,
    ticketData?.ticket?.id,
    ticketData?.ticket?.status,
    ticketData?.ticket?.sessionId,
  ]);

  // Desktop recorder "Open web portal" → /sessions/new?sessionId=… (resume clips / combine)
  useEffect(() => {
    if (hasTicketFlow || !sessionIdFromUrl || desktopSessionInitStarted.current) return;
    desktopSessionInitStarted.current = true;

    (async () => {
      setDesktopSessionInitializing(true);
      setTicketInitError(null);
      try {
        await initializeRecordingSession({}, { openRecordingFrame: false });
      } catch (error: unknown) {
        console.error('Failed to resume desktop recording session:', error);
        const detail = readApiErrorMessage(error, 'Could not open this recording session.');
        setTicketInitError(detail);
        toast.error(detail, { id: 'desktop-session-resume' });
        setShowPreRecordingForm(true);
      } finally {
        setDesktopSessionInitializing(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per sessionId / retry
  }, [hasTicketFlow, sessionIdFromUrl, desktopSessionInitAttempt]);

  const handlePreRecordingSubmit = useCallback(
    async (data: PreRecordingData) => {
      try {
        await initializeRecordingSession(data, { openRecordingFrame: true });
      } catch (error) {
        console.error('Failed to create session for recording:', error);
        toast.error(readApiErrorMessage(error, 'Failed to initialize session. Please try again.'));
      }
    },
    [initializeRecordingSession]
  );

  const handleCancelPreRecording = useCallback(() => {
    if (hasTicketFlow) {
      navigate('/my-tickets');
      return;
    }
    setShowPreRecordingForm(false);
  }, [hasTicketFlow, navigate]);

  const handleCancelSession = useCallback(async () => {
    // If there's a session in progress, cancel it
    if (currentSessionId) {
      try {
        await api.put(`/sessions/${currentSessionId}`, {
          status: 'cancelled'
        });
        queryClient.invalidateQueries('sessions');
        queryClient.invalidateQueries('in-progress-videos');
      } catch (error) {
        console.error('Failed to cancel session:', error);
      }
    }
    
    setShowPostRecordingForm(false);
    setShowRecordingFrame(false);
    setCombinedVideoBlob(null);
    setCombinedVideoDuration(0);
    setSessionActive(false);
    setCurrentSessionId(null);
    clearRecordedVideos();
  }, [clearRecordedVideos, currentSessionId, queryClient]);

  const totalDuration = recordedVideos.reduce((sum, video) => sum + video.duration, 0);

  // Show error if ticket fetch failed
  if (ticketId && ticketError) {
    const errorMessage = (ticketError as any)?.response?.data?.error?.message || 
                         (ticketError as any)?.response?.data?.message || 
                         'Failed to load ticket';
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="card bg-red-50 border-red-200">
          <div className="card-content">
            <div className="flex items-center space-x-2 text-red-800 mb-4">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Access Denied</span>
            </div>
            <p className="text-red-700 mb-4">{errorMessage}</p>
            <div className="flex gap-3">
              <Link to="/my-tickets">
                <Button variant="outline" leftIcon={<ArrowRight className="h-4 w-4" />}>
                  Back to My Tickets
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (hasTicketFlow && ticketInitError) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="card bg-red-50 border-red-200">
          <div className="card-content">
            <div className="flex items-center space-x-2 text-red-800 mb-4">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Could not start recording</span>
            </div>
            <p className="text-red-700 mb-4">{ticketInitError}</p>
            <div className="flex gap-3">
              <Button variant="primary" onClick={retryTicketInit}>
                Try again
              </Button>
              <Link to="/my-tickets">
                <Button variant="outline" leftIcon={<ArrowRight className="h-4 w-4" />}>
                  Back to My Tickets
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasTicketFlow && ticketInitError && sessionIdFromUrl) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="card bg-red-50 border-red-200">
          <div className="card-content">
            <div className="flex items-center space-x-2 text-red-800 mb-4">
              <AlertCircle className="h-5 w-5" />
              <span className="font-medium">Could not open recording session</span>
            </div>
            <p className="text-red-700 mb-4">{ticketInitError}</p>
            <p className="text-sm text-red-600 mb-4 font-mono break-all">Session: {sessionIdFromUrl}</p>
            <div className="flex gap-3">
              <Button
                variant="primary"
                onClick={() => {
                  desktopSessionInitStarted.current = false;
                  setTicketInitError(null);
                  setShowPreRecordingForm(false);
                  setDesktopSessionInitAttempt((n) => n + 1);
                }}
              >
                Try again
              </Button>
              <Link to="/">
                <Button variant="outline">Go to dashboard</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while fetching ticket or preparing session
  if (hasTicketFlow && (ticketLoading || ticketInitializing)) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600">
            {ticketLoading ? 'Loading ticket…' : 'Preparing recording session…'}
          </span>
        </div>
      </div>
    );
  }

  if (!hasTicketFlow && desktopSessionInitializing) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600">Opening recording session…</span>
        </div>
      </div>
    );
  }

  // Ad-hoc sessions (no ticket): legacy pre-recording form
  if (showPreRecordingForm && !hasTicketFlow) {
    return (
      <PreRecordingForm
        onStartRecording={handlePreRecordingSubmit}
        onCancel={handleCancelPreRecording}
      />
    );
  }

  // Show post-recording form if session is complete
  if (showPostRecordingForm && combinedVideoBlob) {
    return (
      <PostRecordingForm
        recordingBlob={combinedVideoBlob}
        recordingDuration={combinedVideoDuration}
        onSaveSession={handleSaveSession}
        onCancel={handleCancelSession}
        preRecordingData={preRecordingData}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      {hasTicketFlow && ticketData?.ticket && <TicketRecordingHeader ticket={ticketData.ticket} />}

      <div className="mb-6 sm:mb-8 text-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          {hasTicketFlow ? 'Record session video' : 'New ETR Video'}
        </h1>
        <p className="mt-2 text-sm sm:text-base text-gray-600">
          {hasTicketFlow
            ? 'Capture screen recordings for this ticket, then combine and upload the final video.'
            : 'Record multiple videos and combine them into one ETR video.'}
        </p>
      </div>

      <div className="space-y-6">
        {/* Browser Support Check */}
        {!isSupported && (
          <div className="card bg-yellow-50 border-yellow-200">
            <div className="card-content">
              <div className="flex items-center space-x-2 text-yellow-800">
                <AlertCircle className="h-5 w-5" />
                <span className="font-medium">Screen Recording Not Supported</span>
              </div>
              <p className="mt-2 text-sm text-yellow-700">
                Your browser doesn't support screen recording. Please use Chrome, Firefox, or Edge.
              </p>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="card bg-red-50 border-red-200">
            <div className="card-content">
              <div className="flex items-center space-x-2 text-red-800">
                <AlertCircle className="h-5 w-5" />
                <span className="font-medium">Recording Error</span>
              </div>
              <p className="mt-2 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Recorded Videos List */}
        {recordedVideos.length > 0 && (
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Recorded Videos ({recordedVideos.length})
                </h2>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleVideoPreview}
                    leftIcon={<Eye className="h-4 w-4" />}
                  >
                    {showVideoPreview ? 'Hide Preview' : 'Preview & Sort'}
                  </Button>
                </div>
              </div>
            </div>
            <div className="card-content">
              {showVideoPreview ? (
                <VideoSortableList
                  videos={orderedVideos}
                  onVideosReorder={handleVideosReorder}
                  onVideoDelete={handleDeleteRecording}
                />
              ) : (
                <div className="space-y-3">
                  {recordedVideos.map((video, index) => (
                    <div key={video.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                          <Video className="h-6 w-6 text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            Recording {index + 1}
                          </p>
                          <p className="text-sm text-gray-600">
                            Duration: {formatDuration(video.duration)} • Size: {formatFileSize(video.blob.size)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Total Duration</p>
                    <p className="text-lg font-bold text-primary-600">{formatDuration(totalDuration)}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {showVideoPreview && (
                      <p className="text-sm text-gray-500">
                        Drag videos to reorder • Click preview to sort
                      </p>
                    )}
                    <Button
                      variant="primary"
                      size="md"
                      onClick={handleEndSession}
                      disabled={isCombining || isScreenRecording}
                      isLoading={isCombining}
                      leftIcon={!isCombining ? <CheckCircle className="h-4 w-4" /> : undefined}
                    >
                      {isCombining ? 'Combining...' : 'End Session & Combine Videos'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Action Card */}
        <div className="card">
          <div className="card-content text-center py-12">
            <div className="mb-8">
              <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-primary-100 mb-4">
                <Video className="h-8 w-8 text-primary-600" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                {sessionActive ? 'Session in Progress' : 'Ready to Record?'}
              </h2>
              <p className="text-gray-600 max-w-md mx-auto">
                {sessionActive
                  ? desktopRecorderOnly
                    ? 'Session is active. Use the desktop recorder to upload clips, then combine when finished.'
                    : 'Your session is active. Record multiple videos and combine them when finished.'
                  : desktopRecorderOnly
                    ? 'Start a session, then open the desktop recorder on this PC.'
                    : 'Click the button below to open a resizable recording window. You can record multiple videos in one session.'}
              </p>
            </div>

            <div className="space-y-4">
              {isScreenRecording ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center space-x-2 text-red-600">
                    <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse"></div>
                    <span className="font-medium">Recording in progress...</span>
                    <span className="font-mono">{formatDuration(currentRecordingDuration)}</span>
                  </div>
                  <Button
                    variant="danger"
                    size="lg"
                    onClick={handleStopRecording}
                    leftIcon={<Square className="h-5 w-5" />}
                    className="mx-auto"
                  >
                    Stop Recording
                  </Button>
                </div>
              ) : sessionActive && currentSessionId && desktopRecorderOnly ? (
                <DesktopClipsPanel
                  sessionId={currentSessionId}
                  ticketId={ticketId}
                  clips={desktopClips}
                  isLoading={desktopClipsLoading}
                  sessionMissing={desktopSessionMissing}
                  onRefresh={() => void refetchDesktopClips()}
                  onResetSession={() => void handleResetRecordingSession()}
                />
              ) : (
                <div className="flex flex-col items-center gap-3">
                  {hasTicketFlow && !sessionActive && (
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => void handleStartTicketRecordingSession()}
                      className="mx-auto w-full max-w-lg"
                    >
                      Start recording session
                    </Button>
                  )}
                  {sessionActive && currentSessionId && (
                    <DesktopClipsPanel
                      sessionId={currentSessionId}
                      ticketId={ticketId}
                      clips={desktopClips}
                      isLoading={desktopClipsLoading}
                      sessionMissing={desktopSessionMissing}
                      onRefresh={() => void refetchDesktopClips()}
                      onResetSession={() => void handleResetRecordingSession()}
                    />
                  )}
                  {!desktopRecorderOnly && (
                    <Button
                      variant={sessionActive && currentSessionId ? 'outline' : 'primary'}
                      size="lg"
                      onClick={handleOpenRecordingFrame}
                      disabled={!isSupported || isCombining}
                      leftIcon={<Play className="h-5 w-5" />}
                      className="mx-auto"
                    >
                      {sessionActive ? 'Record in browser (legacy)' : 'Open Recording Window'}
                    </Button>
                  )}
                </div>
              )}
              
              <p className="text-sm text-gray-500">
                {sessionActive
                  ? desktopRecorderOnly
                    ? 'Upload all clips from the desktop app, adjust order above if needed, then End Session & combine.'
                    : 'Use the desktop recorder for SmartPSS (recommended), or browser recording. Upload clips, then End Session & combine.'
                  : desktopRecorderOnly
                    ? 'Start a session, then use the desktop recorder on this branch PC.'
                    : 'Start a session, then open the desktop recorder or browser recording window.'}
              </p>
            </div>
          </div>
        </div>

        {sessionActive &&
          currentSessionId &&
          desktopClips.length > 0 &&
          recordedVideos.length === 0 && (
            <div className="card border-emerald-200 bg-emerald-50/40">
              <div className="card-content">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-gray-900">Finish session</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {desktopClips.length} clip(s) on the server. Reorder in the list above if needed, then combine
                      into one final video.
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleEndSession}
                    disabled={isCombining || isScreenRecording}
                    isLoading={isCombining}
                    leftIcon={!isCombining ? <CheckCircle className="h-4 w-4" /> : undefined}
                    className="shrink-0"
                  >
                    {isCombining ? 'Combining...' : 'End Session & Combine Videos'}
                  </Button>
                </div>
              </div>
            </div>
          )}

        {/* Instructions */}
        <div className="card bg-blue-50 border-blue-200">
          <div className="card-content">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">How it works:</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800 mb-4">
              <li>Start a session (form or ticket flow)</li>
              <li>
                Click <strong>Open desktop recorder</strong> — sign in, pick monitor / window / region, record clips
                (Stop &amp; upload each segment)
              </li>
              <li>Watch uploaded clips appear on this page; reorder if needed</li>
              <li>Reorder clips on this page if needed, then click <strong>End Session &amp; Combine Videos</strong></li>
              {!desktopRecorderOnly && (
                <li className="text-blue-700">
                  Optional: <strong>Record in browser (legacy)</strong> if the desktop app is not installed
                </li>
              )}
            </ol>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Recording SmartPSS / NVR camera feeds</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-blue-800">
              <li>
                Prefer <strong>Entire screen</strong> (full monitor), not the SmartPSS window — window capture often
                records the app chrome but shows a black box where the video plays.
              </li>
              <li>
                In SmartPSS: <strong>System Config → disable hardware acceleration</strong> (Dahua recommends this for
                reliable playback).
              </li>
              <li>Use the 32-bit (x86) SmartPSS client if the live view stays black with Intel integrated graphics.</li>
              <li>If preview is black, stop and change SmartPSS or sharing settings before recording the full session.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Popup Recording Frame */}
      {!desktopRecorderOnly && (
        <PopupRecordingFrame
          isOpen={showRecordingFrame}
          onClose={handleCloseRecordingFrame}
          onStartRecording={handleStartRecording}
          onStopRecording={handleStopRecording}
          isRecording={isScreenRecording}
          previewStream={previewStream}
          captureWarning={captureWarning}
        />
      )}
      
      <VideoProgressModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        currentStep={progressStep}
        progress={progressValue}
        message={progressMessage}
        canClose={progressStep === 'completed'}
      />
    </div>
  );
};

export default NewSession;